'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { IoCaretBackSharp } from 'react-icons/io5';
import { z } from 'zod';

import Alert from '@components/Alert';
import { pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { Input, Button } from '@components/Form';
import { Link } from '@lib/navigation';
import { fetchApi } from '@utils/api/client';

const schema = z.object({
  email: z.string().email(),
});

export default function ForgottenPasswordForm() {
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const locale = useLocale();
  const t = useTranslations('main');

  const onSubmit = handleSubmit(async ({ email }) => {

    const resetPasswordResponse = await fetchApi('/auth/reset-password', {
      method: 'POST',
      onFailure: 'continue',
      body: JSON.stringify({
        email: email,
        lang: locale
      }),
    });

    // If user not exists
    if (resetPasswordResponse.status === 404) {
      setError('email', { message: t('message.account.errors.passwordResetFailure') });
      return;
    }

    // If user not activated
    if (resetPasswordResponse.status === 412) {
      setError('email', { message: t('message.account.errors.emailNotActivatedFailure') });
      return;
    }

    // other error
    if (resetPasswordResponse.status !== 200) {
      setError('email', { message: t('message.account.errors.emailResendFailure') });
      return;
    }

    pushSimpleGAEvent('transinfo_password_change', {
      transinfo_is_logged_in: 'false',
      transinfo_is_newsletter_user: 'false',
      transinfo_user_email: email,
      transinfo_user_provider: 'wordpress',
      locale: locale,
      transinfo_view_category: 'password-change-from-link'
    });
  });

  return (
    <form
      onSubmit={onSubmit}
      className="dark:bg-dark-500 bg-white p-4 lg:p-8"
    >
      <h1 className="text-premium-900 mb-3 text-2xl font-bold dark:text-white lg:mb-6 lg:text-4xl">
        {t('label.doesntRememberPassword')}
      </h1>
      <div className="mb-4">
        <Input
          type="email"
          placeholder={t('label.placeholder.email')}
          error={!!errors.email}
          {...register('email')}
          onChange={() => {
            clearErrors();
            reset({}, { keepValues: true });
          }}
        />
      </div>
      {errors.email && (
        <div className="mb-4 text-sm text-red-500">{errors.email.message}</div>
      )}

      <div className="flex flex-col justify-center items-center mb-3 lg:mb-6">
        <Button
          disabled={isSubmitting || isSubmitted}
          loading={isSubmitting}
          className="mb-2"
        >
          {t('label.buttons.sendEmail')}
        </Button>

        {isSubmitted && errors.email === undefined && (
          <Alert className="mb-2 !rounded-lg">{t('message.account.success.passwordResetEmailSent')}</Alert>
        )}
      </div>

      <p className="mb-4 text-sm">{t('label.forgottenPasswordDescription')} </p>

      <div className="flex items-center justify-end">
        <Link
          href="/login"
          rel="nofollow"
          className="text-premium-900 dark:text-white flex items-center text-sm font-semibold"
        >
          {' '}
          <IoCaretBackSharp className="h-3 w-3" />
          {t('label.backToLogin')}
        </Link>
      </div>
    </form>
  );
}
